import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Send, CheckCircle, Loader2, Sparkles, Instagram, Linkedin, HandHeart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import { useConfetti } from '@/hooks/useConfetti';

const Footer = () => {
  const { t, language } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const { fireConfetti } = useConfetti();

  const isValidEmail = (emailValue: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailValue.length <= 254 && emailRegex.test(emailValue.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !isValidEmail(email)) {
      setErrorMessage(language === 'ru' ? 'Введите корректный email' : 'Please enter a valid email');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('fields[email]', email);
      
      await fetch('https://assets.mailerlite.com/jsonp/1423386/forms/175947656365868887/subscribe', {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });

      setStatus('success');
      setEmail('');
      fireConfetti();
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus('error');
      setErrorMessage(language === 'ru' ? 'Что-то пошло не так. Попробуйте позже.' : 'Something went wrong. Please try again.');
    }
  };

  const loc = (ru: string, de: string, en: string) =>
    language === 'ru' ? ru : language === 'de' ? de : en;

  const newsletterTexts = {
    title: loc('Оставайся любопытным', 'Bleib neugierig', 'Stay curious'),
    subtitle: loc(
      'Периодические письма о мозге, разуме и о том, как они на самом деле работают.',
      'Gelegentliche E-Mails \u00fcber das Gehirn, den Geist und wie sie wirklich funktionieren.',
      'Occasional emails about the brain, the mind, and how they actually work.'
    ),
    placeholder: loc('Ваш email', 'Deine E-Mail', 'Your email'),
    button: loc('Подписаться', 'Abonnieren', 'Subscribe'),
    success: loc('Готово! Первое письмо уже в пути.', 'Geschafft! Deine erste E-Mail ist unterwegs.', "You're in! Your first email is on the way."),
  };

  const navLinks = [
    { to: '/articles', label: t.nav.articles },
    { to: '/games', label: t.nav.games },
    { to: '/exercises', label: t.nav.exercises },
    { to: '/questionnaires', label: t.nav.questionnaires },
  ];

  const infoLinks = [
    { to: '/what-is', label: language === 'ru' ? 'Что это' : 'What is Oh Mind Guide' },
    { to: '/about', label: language === 'ru' ? 'Обо мне' : 'About' },
    { to: '/faq', label: 'FAQ' },
    { to: '/standards', label: language === 'ru' ? 'Стандарты' : 'Standards' },
    { to: '/saved', label: language === 'ru' ? 'Сохранённое' : 'Saved' },
  ];

  return (
    <footer className="bg-card">
      {/* Colorful top divider - purple */}
      <div className="h-0.5 bg-purple opacity-40" />
      
      {/* Newsletter Section */}
      <div id="contact" className="py-8 md:py-10">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-purple/40 to-coral/30 mb-2">
              <Sparkles className="w-5 h-5 text-purple" />
            </div>
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">
              {newsletterTexts.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {newsletterTexts.subtitle}
            </p>

            {status === 'success' ? (
              <div className="flex flex-col items-center gap-2 py-2">
                <CheckCircle className="w-10 h-10 text-green-600" />
                <p className="text-center font-medium text-foreground text-sm">
                  {newsletterTexts.success}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  placeholder={newsletterTexts.placeholder}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === 'error') setStatus('idle');
                  }}
                  className="flex-1 bg-background border-border focus:border-primary focus:ring-primary/30 h-9"
                  disabled={status === 'loading'}
                />
                <Button 
                  type="submit" 
                  size="sm"
                  className="bg-foreground text-background hover:bg-foreground/90 h-9"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {newsletterTexts.button}
                    </>
                  )}
                </Button>
              </form>
            )}
            {status === 'error' && errorMessage && (
              <p className="text-sm text-red-600 mt-2">{errorMessage}</p>
            )}
          </div>
        </div>
      </div>

      {/* Colorful divider - pink */}
      <div className="h-0.5 bg-secondary opacity-40" />
      
      {/* Let's Connect Section */}
      <div className="py-6">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center">
            <HandHeart className="w-8 h-8 text-secondary" />
          </div>
          <h3 className="text-xl font-bold mb-2 text-secondary">
            {t.home.contact.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
            {t.home.contact.subtitle}
          </p>
          <div className="flex flex-col items-center gap-3">
            <Button asChild size="sm" className="bg-gradient-to-r from-secondary to-accent text-white hover:shadow-glow transition-all duration-300 rounded-full px-5 py-2 font-medium">
              <a href="mailto:margarita@ohmindguide.com" className="inline-flex items-center gap-2">
                <Mail size={16} />
                {t.home.contact.emailBtn}
              </a>
            </Button>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.instagram.com/ohmindguide/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-secondary transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a 
                href="https://www.linkedin.com/in/margarita-popelnyukhova-59a391352/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-muted-foreground hover:text-secondary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Colorful divider - secondary/pink */}
      <div className="h-0.5 bg-secondary opacity-40" />
      
      {/* Navigation Links */}
      <div className="py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8">
            {/* Main pillars */}
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Separator on desktop */}
            <div className="hidden md:block w-px h-3 bg-border" />
            
            {/* Info links */}
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {infoLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Colorful divider - accent/orange */}
      <div className="h-0.5 bg-accent opacity-40" />
      
      {/* Trust & Copyright Section */}
      <div className="py-6">
        <div className="container mx-auto px-4 text-center space-y-3">
          {/* Author Attribution */}
          <p className="text-xs text-muted-foreground">
            {language === 'ru' ? 'Создано: ' : 'Created by '}
            <Link to="/about" className="text-primary hover:underline">
              {language === 'ru' 
                ? 'Маргарита Попельнюхова, MD, MSc (Нейронаука)' 
                : 'Margarita Popelnyukhova, MD, MSc (Neuroscience)'}
            </Link>
          </p>
          
          {/* Trust Links */}
          <p className="text-xs text-muted-foreground">
            <Link to="/standards" className="text-primary hover:underline">
              {language === 'ru' ? 'Редакционные стандарты' : 'Editorial standards'}
            </Link>
            {' · '}
            {language === 'ru' 
              ? 'Образовательный контент. Не является медицинской консультацией.' 
              : 'Educational content only. Not medical advice.'}
          </p>
          
          {/* Copyright */}
          <p className="text-xs text-muted-foreground">
            {t.home.footer.copyright.replace("{year}", currentYear.toString())}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
